import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AuthService } from '../../services/apis/usuario/auth.service';
import { IaAssistenteService } from '../../services/utils/ia-assistente.service';
import { IaChatComponent } from '../ia-chat/ia-chat.component';

interface IaAssistenteEstado {
  aberto: boolean;
  idConversa?: number;
}

/**
 * Assistente ATS flutuante, global na aplicação (montado no app.component,
 * fora do router-outlet — por isso a conversa sobrevive às navegações).
 * Ele decide sozinho quando aparecer: apenas em telas autenticadas, fora das
 * páginas do próprio assistente (/home e /home/chat) e nunca no mobile (CSS).
 * O query param ?iaConversa=<id> em qualquer navegação abre o drawer naquela conversa.
 * A abertura é comandada pelo ícone do menu lateral via IaAssistenteService.
 */
@Component({
  selector: 'app-ia-assistente',
  standalone: true,
  imports: [IaChatComponent],
  templateUrl: './ia-assistente.component.html',
  styleUrl: './ia-assistente.component.css',
})
export class IaAssistenteComponent implements OnInit, OnDestroy {
  private static readonly STORAGE_KEY = 'ia_assistente_estado';

  // Rotas sem AuthGuard (mesma lista do banner-alerta) — o assistente nunca aparece nelas.
  private static readonly ROTAS_PUBLICAS_PREFIXOS = ['/cobranca', '/auth', '/portal', '/documentacao-publica'];

  public visivel: boolean = false;
  public aberto: boolean = false;
  public idConversa?: number;

  public readonly chips: string[] = ['O que vence hoje?', 'Resumo do mês'];

  @ViewChild(IaChatComponent) private iaChat?: IaChatComponent;

  private subscriptions = new Subscription();
  private readonly isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    private router: Router,
    private authService: AuthService,
    private iaAssistenteService: IaAssistenteService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) return; // SSR: o assistente só existe no browser

    this.restaurarEstado();
    this.avaliar();

    this.subscriptions.add(
      this.router.events
        .pipe(filter(evt => evt instanceof NavigationEnd))
        .subscribe(() => this.avaliar())
    );

    // Comando do ícone do menu lateral (HeaderComponent).
    this.subscriptions.add(
      this.iaAssistenteService.alternar$.subscribe(() => {
        if (!this.visivel) return;
        this.aberto ? this.fechar() : this.abrir();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public abrir(): void {
    this.aberto = true;
    this.iaAssistenteService.aberto.set(true);
    this.salvarEstado();
  }

  public fechar(): void {
    this.aberto = false;
    this.iaAssistenteService.aberto.set(false);
    this.salvarEstado();
  }

  public novaConversa(): void {
    this.idConversa = undefined;
    this.iaChat?.novaConversa();
    this.salvarEstado();
  }

  public onIdConversaChange(idConversa: number): void {
    this.idConversa = idConversa;
    this.salvarEstado();
  }

  /** Reavaliada a cada navegação: visibilidade + abertura via ?iaConversa. */
  private avaliar(): void {
    const url = this.router.url;

    this.visivel = !this.emRotaPublica(url)
      && !this.emTelaDoAssistente(url)
      && this.authService.isAuthenticated();

    this.iaAssistenteService.visivel.set(this.visivel);

    if (!this.visivel) return;

    const urlTree = this.router.parseUrl(url);
    const idParam = Number(urlTree.queryParams['iaConversa']);

    if (idParam > 0) {
      this.idConversa = idParam;
      this.aberto = true;
      this.salvarEstado();

      // Limpa o param da URL para não re-disparar em F5/histórico.
      delete urlTree.queryParams['iaConversa'];
      this.router.navigateByUrl(urlTree, { replaceUrl: true });
    }
  }

  /** Raiz ('/') é o login — tolera query string (ex.: '/?token=...' do super-acesso). */
  private emRotaPublica(url: string): boolean {
    if (url === '/' || url.startsWith('/?')) return true;
    return IaAssistenteComponent.ROTAS_PUBLICAS_PREFIXOS.some(prefixo => url.startsWith(prefixo));
  }

  /** /home e /home/chat JÁ são o assistente — o drawer não aparece nelas. */
  private emTelaDoAssistente(url: string): boolean {
    return url === '/home' || url.startsWith('/home?') || url.startsWith('/home/chat');
  }

  private salvarEstado(): void {
    try {
      const estado: IaAssistenteEstado = { aberto: this.aberto, idConversa: this.idConversa };
      sessionStorage.setItem(IaAssistenteComponent.STORAGE_KEY, JSON.stringify(estado));
    } catch {
      /* sessionStorage indisponível — ignora */
    }
  }

  private restaurarEstado(): void {
    try {
      const raw = sessionStorage.getItem(IaAssistenteComponent.STORAGE_KEY);
      if (!raw) return;

      const estado = JSON.parse(raw) as IaAssistenteEstado;
      this.aberto = estado.aberto === true;
      this.iaAssistenteService.aberto.set(this.aberto);
      this.idConversa = estado.idConversa || undefined;
    } catch {
      /* estado corrompido — começa fechado */
    }
  }
}
